import { useEffect, useRef, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import api from "@/lib/api";

// UI preferences + last-seen cursor live in sessionStorage instead of
// localStorage. Rationale:
//   1. XSS-scoped: any injected script gets a shorter blast radius.
//   2. Multi-user devices: if user A logs out and user B logs in on the
//      same laptop, they don't inherit A's cursor / muted state.
//   3. These values are transient UI polish, not high-value data — losing
//      them on tab close is acceptable.
// Wrapped in a null-safe helper because Safari private mode sometimes
// throws on any storage access.
const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
};

const SEEN_KEY = "pg.partner.lastSeenTxn";
const SOUND_KEY = "pg.partner.notifSound";
const PUSH_KEY = "pg.partner.notifPush";

function playChime() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const tones = [
            { f: 880, t: 0 },
            { f: 1320, t: 0.12 },
        ];
        tones.forEach(({ f, t }) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = f;
            gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
            gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + t + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.32);
            osc.connect(gain).connect(ctx.destination);
            osc.start(ctx.currentTime + t);
            osc.stop(ctx.currentTime + t + 0.34);
        });
        setTimeout(() => ctx.close(), 800);
    } catch { /* sound is best-effort */ }
}

function showSystemNotification({ count, top }) {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
        const title = count > 1 ? `${count} new payments submitted` : "New payment submitted";
        const body = `${top.customer_username} · ₹${Number(top.amount).toLocaleString("en-IN")}${top.utr ? ` · UTR ${top.utr}` : ""}`;
        const n = new Notification(title, {
            body,
            tag: "pg-new-txn",
            renotify: true,
            silent: false,
            icon: "/favicon.ico",
        });
        n.onclick = () => {
            window.focus();
            window.location.assign("/partner/approvals");
            n.close();
        };
    } catch { /* push is best-effort */ }
}

export function useTxnNotifications({ enabled = true, intervalMs = 6000 } = {}) {
    const [latest, setLatest] = useState(null);
    const [soundOn, setSoundOn] = useState(() => store.get(SOUND_KEY) !== "off");
    const [pushOn, setPushOn] = useState(() =>
        store.get(PUSH_KEY) === "on" &&
        typeof Notification !== "undefined" &&
        Notification.permission === "granted",
    );
    const lastSeenRef = useRef(store.get(SEEN_KEY) || null);
    const initialisedRef = useRef(false);

    const setSound = (v) => {
        setSoundOn(v);
        store.set(SOUND_KEY, v ? "on" : "off");
    };

    const requestPushPermission = async () => {
        if (typeof Notification === "undefined") {
            alert("Your browser doesn't support notifications.");
            return false;
        }
        if (Notification.permission === "granted") {
            setPushOn(true);
            store.set(PUSH_KEY, "on");
            return true;
        }
        if (Notification.permission === "denied") {
            alert("Notifications are blocked in your browser settings. Re-enable them in the address-bar lock icon → Site settings.");
            return false;
        }
        const res = await Notification.requestPermission();
        const granted = res === "granted";
        setPushOn(granted);
        store.set(PUSH_KEY, granted ? "on" : "off");
        return granted;
    };

    const setPush = async (v) => {
        if (v) await requestPushPermission();
        else { setPushOn(false); store.set(PUSH_KEY, "off"); }
    };

    useEffect(() => {
        if (!enabled) return;
        let stopped = false;
        const tick = async () => {
            try {
                const r = await api.get("/partner/transactions?status=submitted&limit=10");
                const items = r.data.items || [];
                if (items.length === 0) return;
                const newest = items[0];
                if (!initialisedRef.current) {
                    initialisedRef.current = true;
                    if (!lastSeenRef.current) {
                        lastSeenRef.current = newest.created_at;
                        store.set(SEEN_KEY, newest.created_at);
                    }
                    return;
                }
                const fresh = items.filter((t) => !lastSeenRef.current || t.created_at > lastSeenRef.current);
                if (fresh.length === 0) return;
                lastSeenRef.current = newest.created_at;
                store.set(SEEN_KEY, newest.created_at);
                if (soundOn) playChime();
                if (pushOn) showSystemNotification({ count: fresh.length, top: fresh[0] });
                setLatest({ count: fresh.length, top: fresh[0], at: Date.now() });
            } catch { /* network blip — keep polling */ }
        };
        tick();
        const id = setInterval(() => { if (!stopped) tick(); }, intervalMs);
        return () => { stopped = true; clearInterval(id); };
    }, [enabled, intervalMs, soundOn, pushOn]);

    return { latest, soundOn, setSound, pushOn, setPush, clear: () => setLatest(null) };
}

export function NotifBell({ soundOn, setSound, pushOn, setPush }) {
    const pushSupported = typeof Notification !== "undefined";
    return (
        <div className="flex items-center gap-1">
            <button
                data-testid="notif-sound-toggle"
                onClick={() => setSound(!soundOn)}
                title={soundOn ? "Sound on — click to mute" : "Sound off — click to enable"}
                className={`relative w-9 h-9 rounded-full flex items-center justify-center text-sm transition-colors ${soundOn ? "text-emerald-600 bg-emerald-50 hover:bg-emerald-100" : "text-slate-400 bg-slate-100 hover:bg-slate-200"}`}
            >
                {soundOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                {soundOn && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
            </button>
            {pushSupported && (
                <button
                    data-testid="notif-push-toggle"
                    onClick={() => setPush(!pushOn)}
                    title={pushOn
                        ? "Browser notifications on — click to disable"
                        : "Enable browser notifications (works even when this tab isn't focused)"}
                    className={`relative w-9 h-9 rounded-full flex items-center justify-center text-sm transition-colors ${pushOn ? "text-blue-600 bg-blue-50 hover:bg-blue-100" : "text-slate-400 bg-slate-100 hover:bg-slate-200"}`}
                >
                    <BellRing className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

export function NotifToast({ latest, onDismiss, onView }) {
    if (!latest) return null;
    const t = latest.top;
    return (
        <div data-testid="notif-toast"
            className="fixed bottom-5 right-5 z-[120] w-[340px] card border-emerald-200 ring-2 ring-emerald-100 shadow-lg fade-up overflow-hidden">
            <div className="p-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                        <Bell className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">
                            {latest.count > 1 ? `${latest.count} new payments submitted` : "New payment submitted"}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                            <span className="font-medium text-slate-700">{t.customer_username}</span>
                            {" · "}₹{Number(t.amount).toLocaleString("en-IN")}
                            {t.utr && <span className="font-mono text-[11px] ml-1">UTR {t.utr.slice(-6)}</span>}
                        </div>
                        <div className="flex gap-2 mt-3">
                            <button data-testid="notif-view" onClick={onView} className="btn-emerald px-3 py-1.5 rounded-md text-xs">
                                View approvals
                            </button>
                            <button data-testid="notif-dismiss" onClick={onDismiss} className="btn-secondary px-3 py-1.5 rounded-md text-xs">
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
